import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Reveal, SectionLabel, BtnRed, BtnGhost, CtaBanner } from '../components/common';
import { images, homeSolutions, homeSpaces, methodSteps, possibilities, homeProjects } from '../mock/mock';

export default function Home() {
  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img src={images.heroStage} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0d]/80 via-[#0b0b0d]/60 to-[#0b0b0d]" />
        <div className="absolute inset-0 mt-texture opacity-60" />

        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 pt-28 w-full">
          <Reveal>
            <p className="font-display uppercase tracking-[0.3em] text-xs md:text-sm text-white/70 mb-6">
              Música, cultura y entretenimiento con sentido
            </p>
          </Reveal>

          <Reveal delay={100}>
            <div className="inline-block mt-skew-banner px-7 py-5 md:px-10 md:py-7 max-w-3xl">
              <h1 className="mt-skew-inner font-display font-bold uppercase text-white text-3xl md:text-5xl lg:text-6xl leading-[0.98]">
                Hay espacios que todavía no han encontrado su ritmo.
              </h1>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <p className="mt-8 max-w-xl text-white/75 text-base md:text-lg leading-relaxed">
              MusicTeam diseña y produce experiencias capaces de transformar un lugar,
              conectar con su público y convertir una actividad en algo que merece ser recordado.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnRed to="/hablemos">Encuentra el ritmo de tu espacio</BtnRed>
              <BtnGhost to="/hazlo-posible">Descubre cómo lo hacemos</BtnGhost>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="mt-scroll-hint w-px h-10 bg-white/40" />
        </div>
      </section>

      {/* ===== POSICIONAMIENTO ===== */}
      <section className="relative py-24 md:py-32 mt-checker">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <p className="font-display uppercase tracking-[0.28em] text-xs text-[var(--mt-muted)] mb-8">
              Antes de que suene la primera nota
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl leading-tight text-white">
              No programamos actividades.
              <span className="block text-[var(--mt-red)]">Activamos espacios.</span>
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-8 text-white/60 max-w-2xl mx-auto leading-relaxed">
              No se trata solo de llenar un espacio. Se trata de entender qué puede suceder en él,
              a quién queremos atraer y qué hará que las personas quieran volver. Por eso no empezamos
              buscando un grupo: empezamos escuchando el espacio.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===== PRESENTACIÓN (split) ===== */}
      <section className="bg-[var(--mt-bg-alt)] py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal>
            <div>
              <SectionLabel>El ritmo no aparece por casualidad</SectionLabel>
              <h3 className="font-display font-bold uppercase text-2xl md:text-4xl leading-tight text-white">
                Una idea puede emocionar. Pero también tiene que funcionar.
              </h3>
              <p className="mt-6 text-white/65 leading-relaxed">
                MusicTeam reúne criterio artístico, experiencia técnica y capacidad de producción
                para convertir una intención en una experiencia real. Conectamos artistas, técnicos,
                proveedores, espacios y públicos dentro de una misma propuesta.
              </p>
              <div className="mt-8">
                <BtnRed to="/hazlo-posible">Hazlo posible</BtnRed>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[var(--mt-red)]" />
              <img src={images.crowd} alt="Público en un concierto" className="w-full h-[420px] object-cover" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[var(--mt-red)]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== SOLUCIONES ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <SectionLabel>De la idea al escenario</SectionLabel>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white max-w-2xl leading-tight">
              Cada proyecto necesita una combinación diferente.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-x-16 gap-y-10 mt-14">
            {homeSolutions.map((s, i) => (
              <Reveal key={s.n} delay={(i % 2) * 100}>
                <div className="group border-t border-white/10 pt-6 flex gap-6">
                  <span className="mt-num text-4xl md:text-5xl opacity-90 group-hover:scale-110 transition-transform origin-left">
                    {s.n}
                  </span>
                  <div>
                    <h3 className="font-display font-semibold uppercase text-lg md:text-xl text-white tracking-wide">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-white/55 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={100}>
            <div className="mt-14">
              <BtnRed to="/encuentra-tu-ritmo">Descubre cómo lo hacemos posible</BtnRed>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== ESPACIOS ===== */}
      <section className="bg-[var(--mt-bg-alt)] py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <SectionLabel>Cada lugar tiene algo que contar</SectionLabel>
                <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight max-w-xl">
                  Cada lugar tiene su propio ritmo
                </h2>
              </div>
              <Link
                to="/encuentra-tu-ritmo"
                className="font-display uppercase tracking-wider text-sm text-white/70 hover:text-[var(--mt-red)] inline-flex items-center gap-2 transition-colors"
              >
                Encuentra tu ritmo <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-5">
            {homeSpaces.map((sp, i) => (
              <Reveal key={sp.title} delay={i * 120}>
                <Link to="/encuentra-tu-ritmo" className="mt-space-card group block relative overflow-hidden h-[420px]">
                  <img src={sp.img} alt={sp.title} className="w-full h-full object-cover grayscale brightness-[0.55]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <span className="font-display uppercase tracking-[0.25em] text-xs text-[var(--mt-red)]">{sp.tag}</span>
                  </div>
                  <div className="absolute bottom-6 left-6">
                    <h3 className="font-display font-bold uppercase text-2xl text-white">{sp.title}</h3>
                    <span className="mt-red-bar mt-3 !w-10 !h-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MÉTODO ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <SectionLabel>Lo hacemos suceder</SectionLabel>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white max-w-2xl leading-tight">
              Todo empieza mucho antes de que llegue el público.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {methodSteps.map((m, i) => (
              <Reveal key={m.n} delay={i * 100}>
                <div className="bg-[var(--mt-bg-card)] border border-white/5 p-7 h-full hover:border-[var(--mt-red)]/50 transition-colors">
                  <span className="mt-num text-3xl">{m.n}</span>
                  <h3 className="font-display font-semibold uppercase text-lg text-white mt-4 tracking-wide">{m.title}</h3>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== POSIBILIDADES ===== */}
      <section className="bg-[var(--mt-bg-alt)] py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 md:gap-20">
          <Reveal>
            <div>
              <SectionLabel>Imagina lo que podría suceder</SectionLabel>
              <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight">
                A veces no falta espacio.
                <span className="block text-[var(--mt-red)]">Falta una idea que lo active.</span>
              </h2>
              <p className="mt-6 text-white/60 leading-relaxed">
                Hay espacios esperando una buena razón para cobrar vida.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <ul className="space-y-4">
              {possibilities.map((p, i) => (
                <li key={i} className="flex gap-4 border-b border-white/8 pb-4">
                  <span className="mt-num text-sm pt-1">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-white/70 leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ===== PROYECTOS ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <SectionLabel>En movimiento</SectionLabel>
                <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight max-w-xl">
                  Cuando todas las piezas encuentran su ritmo.
                </h2>
              </div>
              <Link
                to="/en-movimiento"
                className="font-display uppercase tracking-wider text-sm text-white/70 hover:text-[var(--mt-red)] inline-flex items-center gap-2 transition-colors"
              >
                Ver proyectos <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {homeProjects.map((p, i) => (
              <Reveal key={p.n} delay={i * 120}>
                <Link to="/en-movimiento" className="mt-space-card group block relative overflow-hidden h-[320px]">
                  <img src={p.img} alt={p.n} className="w-full h-full object-cover grayscale brightness-[0.5]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <span className="font-display uppercase tracking-[0.25em] text-xs text-[var(--mt-red)]">{p.n}</span>
                    <p className="mt-2 font-display uppercase text-white text-lg leading-tight">{p.text}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BENEFICIO (red band) ===== */}
      <section className="bg-[var(--mt-red)] py-20 md:py-28">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight">
              Tú ves cómo el espacio cobra vida.
              <span className="block text-black/80">Nosotros hacemos que suceda.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-2 text-white/90 font-display uppercase tracking-wider text-sm">
              <span>// Un único interlocutor</span>
              <span>// Criterio para decidir</span>
              <span>// Producción coherente</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <CtaBanner
        label="Empezamos escuchando"
        title="Tu espacio ya tiene una personalidad."
        redTitle="Vamos a encontrar su ritmo."
        text="Cuéntanos qué espacio gestionas, qué público quieres atraer y qué te gustaría que sucediera."
        cta="Contactar ahora"
        image={images.stageRed}
      />
    </div>
  );
}
